// Loaded on demand by plugins/sentry.client.ts, so the SDK stays out of the entry chunk.
import {
    addIntegration,
    browserTracingIntegration,
    captureException,
    init,
    piniaIntegration
} from '@sentry/nuxt';
// @sentry/nuxt's own vueIntegration is a stub its (now unused) client plugin reads; this is the real one.
// ponytail: transitive dep of @sentry/nuxt (same version); add it explicitly if the import breaks.
import { vueIntegration } from '@sentry/vue';
import type { Pinia } from 'pinia';
import type { App } from 'vue';
import type { Router } from 'vue-router';

export interface ErrorReport {
    error: unknown;
    // 'vue' for component errors, 'app' for Nuxt app errors, 'global' for window error/unhandledrejection
    source: 'vue' | 'app' | 'global';
    info?: string;
}

export function report({ error, source, info }: ErrorReport): void {
    captureException(error, {
        captureContext: { contexts: { nuxt: { info } } },
        mechanism: { handled: false, type: `auto.function.nuxt.${source}-error` }
    });
}

export function initSentry(options: {
    dsn: string;
    tracesSampleRate: number;
    app: App;
    router: Router;
    pinia: Pinia;
}): void {
    init({
        dsn: options.dsn,
        // v11 collects PII by default; keep v10's `sendDefaultPii: false` behaviour
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
                response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }
            },
            httpBodies: [],
            urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            graphQL: { document: false, variables: false }
        },
        integrations: [
            piniaIntegration(options.pinia),
            // errors are reported through the plugin's nuxt hooks; this only adds component context
            // no trackComponents: a span per component during hydration (80 tiles on /products); not worth the overhead
            vueIntegration({ app: options.app, attachErrorHandler: false }),
            browserTracingIntegration({ router: options.router, routeLabel: 'path' })
        ],
        beforeSend(event, hint) {
            // Ignore 4xx errors (client errors): FetchError from the API and createError() 404s from pages
            const statusCode = (hint.originalException as { statusCode?: number } | undefined)?.statusCode;

            if (statusCode && statusCode >= 400 && statusCode < 500) {
                return null;
            }

            return event;
        },
        // every buffered session pays a full DOM snapshot (~1s main thread on /products at 4x CPU throttle)
        replaysOnErrorSampleRate: 0.1,
        replaysSessionSampleRate: options.tracesSampleRate,
        tracesSampleRate: options.tracesSampleRate
    });

    // ponytail: @sentry/replay is a transitive dep of @sentry/nuxt (same version); add it explicitly if the import breaks.
    void import('@sentry/replay').then(({ replayIntegration }) => addIntegration(replayIntegration({
        stickySession: true,
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false
    })));
}
